import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

// Production aur Localhost ke liye sahi BASE_URL configuration
const BASE_URL = window.location.hostname === 'localhost'
    ? 'http://127.0.0.1:8002'
    : 'https://likaiev.com';

const ProductDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const response = await axios.get(`${BASE_URL}/api/products/${id}/`);
                setProduct(response.data);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching product:", error);
                setLoading(false);
            }
        };
        fetchProduct();
    }, [id]);

    // Product Images ke URL ko safely handle karne ke liye helper function
    const getImageUrl = (imgPath) => {
        if (!imgPath) return '/placeholder.jpg';

        // Agar backend IP/port wala URL aaye, toh use BASE_URL se replace karna
        if (imgPath.includes('66.116.250.115:8002') || imgPath.includes('127.0.0.1')) {
            return imgPath.replace(/http:\/\/[^/]+/, BASE_URL);
        }
        if (imgPath.startsWith('http://') || imgPath.startsWith('https://')) {
            return imgPath;
        }
        return `${BASE_URL}${imgPath}`;
    };

    if (loading) return <div className="text-center py-20 text-2xl font-bold">Loading...</div>;
    if (!product) return <div className="text-center py-20 text-xl text-gray-500">Product not found!</div>;

    const mainImage = product.images && product.images.length > 0 ? getImageUrl(product.images[0].image) : "/placeholder.jpg";

    return (
        <div className="bg-gray-50 min-h-screen overflow-x-hidden">
            {/* Banner Section */}
            <div
                className="relative h-[250px] md:h-[350px] w-full flex items-center justify-center mb-8 md:mb-16 overflow-hidden bg-cover bg-center"
                style={{ backgroundImage: "url('/banner1.jpg')" }}
            >
                <div className="absolute inset-0 bg-blue-900/70"></div>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative z-10 text-center px-4 max-w-4xl"
                >
                    <h1 className="text-2xl md:text-5xl font-extrabold text-white tracking-wider mb-2 md:mb-4 uppercase break-words">{product.title}</h1>
                    <div className="w-16 md:w-20 h-1 bg-orange-500 mx-auto"></div>
                </motion.div>
            </div>

            <div className="container mx-auto max-w-6xl px-4 md:px-6 pb-20">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 bg-white p-4 md:p-8 rounded-2xl shadow-xl overflow-hidden">

                    {/* Image Gallery */}
                    <div className="space-y-4 w-full overflow-hidden">
                        <div className="w-full h-72 md:h-80 rounded-xl overflow-hidden shadow-md bg-gray-100">
                            <img
                                src={mainImage}
                                alt={product.title}
                                onClick={() => setSelectedImage(mainImage)}
                                className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition"
                            />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            {product.images && product.images.slice(1).map((imgObj, idx) => {
                                const subImgUrl = getImageUrl(imgObj.image);
                                return (
                                    <div key={idx} className="h-20 md:h-24 rounded-lg overflow-hidden bg-gray-100">
                                        <img
                                            src={subImgUrl}
                                            alt="product sub"
                                            onClick={() => setSelectedImage(subImgUrl)}
                                            className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Info */}
                    <div className="flex flex-col justify-center overflow-hidden">
                        <span className="bg-blue-100 text-blue-900 px-4 py-1 rounded-full font-bold inline-block mb-3 md:mb-4 self-start text-xs md:text-sm">
                            {product.category || "General"}
                        </span>

                        <h1 className="text-2xl md:text-4xl font-bold text-blue-900 mb-4 md:mb-6 break-words leading-tight">{product.title}</h1>

                        <p className="text-gray-600 text-sm md:text-lg leading-relaxed mb-6 md:mb-8 break-words">{product.description}</p>

                        <button
                            onClick={() => navigate('/enquiry')}
                            className="bg-orange-600 text-white px-8 md:px-10 py-3 md:py-4 rounded-lg font-bold hover:bg-blue-900 transition shadow-lg w-full md:w-auto text-center"
                        >
                            ENQUIRE NOW
                        </button>
                    </div>
                </div>
            </div>

            {/* Modal for Zooming Image */}
            <AnimatePresence>
                {selectedImage && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSelectedImage(null)}
                        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                    >
                        <motion.img
                            initial={{ scale: 0.8 }}
                            animate={{ scale: 1 }}
                            src={selectedImage}
                            alt="Zoomed"
                            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
                        />
                        <button
                            className="absolute top-5 right-5 text-white text-3xl font-bold"
                            onClick={() => setSelectedImage(null)}
                        >
                            &times;
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ProductDetails;